import { pipe, Either, Task } from "fp";

import { type, creator, reducer, effects } from "~/Action";
import * as Data from "~/Data";
import * as ELD from "~/ELD";

export namespace Requested {
  export const { TYPE, create, reduce } = pipe(
    type("ELDs resubmit requested" as const),
    creator((submission: Data.Reference.Reference) => ({ submission })),
    reducer(({ state, action: { submission } }) =>
      pipe(
        state.elds.store,
        Data.Store.Get.fromReference({
          ...submission,
          missing: () => {},
          found: (eld) =>
            // eslint-disable-next-line functional/immutable-data
            (eld.submission = ELD.Submission.resend(eld.submission)),
        })
      )
    ),
    effects(({ dispatch, state: { elds, context }, action: { submission } }) =>
      pipe(
        elds.store,
        Data.Store.Get.fromReference({
          ...submission,
          missing: () => {},
          found: ({ submission: { status } }) =>
            ELD.Submission.Status.isResendRequesting(status) &&
            pipe(
              context,
              ELD.Submission.Status.Request.Resubmit.request(submission),
              Task.map((resend) => {
                // eslint-disable-next-line functional/no-expression-statement
                dispatch(Received.create({ submission, resend }));

                // Since we have resent a submission, request a fresh update of the submissions for the company
                // so that we can display the newly generated submission.
                if (Either.isRight(resend)) {
                  // eslint-disable-next-line functional/no-expression-statement
                  dispatch(ELD.Action.Requested.create());
                }
              })
            )(),
        })
      )
    )
  );
}

export namespace Received {
  export const { TYPE, create, reduce } = pipe(
    type("ELDs resubmit received" as const),
    creator(
      (payload: {
        readonly submission: Data.Reference.Reference;
        readonly resend: Either.ErrorOr<
          ELD.Submission.Status.Failure.Resend.Resend
        >;
      }) => payload
    ),
    reducer(({ state, action: { submission, resend } }) =>
      pipe(
        state.elds.store,
        Data.Store.Get.fromReference({
          ...submission,
          missing: () => undefined,
          found: (eld) =>
            // eslint-disable-next-line functional/immutable-data
            (eld.submission.status = pipe(
              eld.submission.status,
              Either.mapLeft(
                (failure): ELD.Submission.Status.Failure.Failure => ({
                  ...failure,
                  resend: Data.Remote.received(resend),
                })
              )
            )),
        })
      )
    )
  );
}
